import { useMemo, Children, isValidElement, forwardRef, createElement, createContext, useContext } from 'react';
import cn from 'classnames';
import { Root, Item, Indicator } from '@radix-ui/react-radio-group';

const getRadioGroupItemValueAsString = value => String(value !== null && value !== void 0 ? value : '');
const findByValue = (values, valueAsString) => values.find(value => getRadioGroupItemValueAsString(value) === valueAsString);
const RadioGroupContext = /*#__PURE__*/createContext({
  disabled: false,
  invalid: false
});
const RadioGroupItem = /*#__PURE__*/forwardRef(function RadioGroupItem(props, ref) {
  const context = useContext(RadioGroupContext);
  const {
    children,
    value,
    ...otherProps
  } = props;
  const isDisabled = context.disabled || props.disabled;
  const className = cn('flex flex-shrink-0 self-start items-center justify-center h-4 w-4 mt-[0.2rem] rounded-full bg-white border-2', {
    'border-grey-dark hover:border-4 focus:yt-focus aria-checked:bg-blue aria-checked:border-blue hover:aria-checked:border-blue-light': !isDisabled && !context.invalid,
    'border-grey cursor-not-allowed aria-checked:bg-blue-light aria-checked:border-blue-light ': isDisabled,
    'border-red hover:border-4 text-red focus:border-red focus:yt-focus-red aria-checked:bg-red aria-checked:border-red': context.invalid && !isDisabled
  });
  const labelClassName = cn('flex items-center gap-2', {
    'cursor-pointer': !isDisabled,
    'cursor-not-allowed text-grey-dark': isDisabled
  }, props.className);
  return createElement("label", {
    className: labelClassName
  }, createElement(Item, Object.assign({}, otherProps, {
    className: className,
    disabled: isDisabled,
    ref: ref,
    value: getRadioGroupItemValueAsString(value)
  }), createElement(Indicator, {
    className: "h-2 w-2 rounded-full bg-white"
  })), children);
});
const useRadioGroup = props => {
  const {
    children,
    defaultValue,
    disabled,
    invalid,
    onChange,
    orientation = 'vertical',
    value,
    ...otherProps
  } = props;
  const values = useMemo(() => {
    const radioGroupItemValues = [];
    Children.forEach(children, child => {
      if (isValidElement(child)) {
        radioGroupItemValues.push(child.props.value);
      }
    });
    return radioGroupItemValues;
  }, [children]);
  const context = useMemo(() => ({
    disabled: disabled !== null && disabled !== void 0 ? disabled : false,
    invalid: invalid !== null && invalid !== void 0 ? invalid : false
  }), [disabled, invalid]);
  let valueProps;

  if (onChange !== undefined) {
    const handleChange = value => onChange(findByValue(values, value));

    valueProps = {
      onValueChange: handleChange,
      value: value !== undefined ? getRadioGroupItemValueAsString(value) : undefined
    };
  } else {
    valueProps = {
      defaultValue: defaultValue !== undefined ? getRadioGroupItemValueAsString(defaultValue) : undefined
    };
  }

  return {
    context,
    props: { ...otherProps,
      ...valueProps,
      children,
      orientation
    }
  };
};
const RadioGroup = /*#__PURE__*/forwardRef(function RadioGroup(props, ref) {
  const {
    context,
    props: otherProps
  } = useRadioGroup(props);
  const className = cn('flex items-start gap-y-2', {
    'flex-wrap gap-x-4 ': otherProps.orientation === 'horizontal',
    'flex-col': otherProps.orientation === 'vertical'
  }, otherProps.className);
  return createElement(RadioGroupContext.Provider, {
    value: context
  }, createElement(Root, Object.assign({}, otherProps, {
    className: className,
    "data-taco": "radio-group",
    ref: ref
  })));
});
RadioGroup.Item = RadioGroupItem;

export { RadioGroup, findByValue, getRadioGroupItemValueAsString, useRadioGroup };
//# sourceMappingURL=RadioGroup.js.map
